import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects';
import { batchActions } from 'redux-batched-actions';
import i18n from 'i18next';
import { CreateExchangeDocument, RefuseExchangeDocument } from '@websky/graphql';
import { createClient } from '../../../../graphql-client';
import { setLastExchange } from '../../../../Checkout/store/order/actions';
import { getLastExchange } from '../../../../Checkout/store/order/selectors';
import { getGqlErrorMessage } from '@shared/lib/getGqlErrorMessage';
import { getInitialSegmentsState } from '../lib/getInitialSegmentsState';
import { getInitialPassengerState } from '../lib/getInitialPassengerState';
import { shouldSkipPassengers } from '@shared/lib/passengers';
import { setError } from './error/actions';
import { setLoading, stopLoading } from './isLoading/actions';
import { setPassengers } from './passengers/actions';
import { setSegments } from './segments/actions';
import { segmentsSagas } from './segments/sagas';
import { clearExchangeVariants } from './exchangeVariants/actions';
import { getExchangeSelectedFlightsInfo } from './exchangeVariants/selectors';
import { exchangeVariantsSagas } from './exchangeVariants/sagas';
import { CREATE_EXCHANGE_ORDER, REFUSE_EXCHANGE_ORDER } from './actions';
import { getCreateExchangeParams } from './selectors';
function createExchangeMutation(params) {
    const client = createClient();
    return client.mutate({
        mutation: CreateExchangeDocument,
        variables: { params },
        fetchPolicy: 'no-cache'
    });
}
function refuseExchangeMutation(id) {
    const client = createClient();
    return client.mutate({
        mutation: RefuseExchangeDocument,
        variables: { id },
        fetchPolicy: 'no-cache'
    });
}
function* createExchangeOrderSaga({ payload }) {
    var _a, _b, _c, _d;
    const createExchangeParams = yield select(getCreateExchangeParams);
    const exchangeSelectedFlightsInfo = yield select(getExchangeSelectedFlightsInfo);
    const mergedCreateExchangeParams = Object.assign(Object.assign({}, createExchangeParams), payload.createExchangeParams);
    if (exchangeSelectedFlightsInfo) {
        mergedCreateExchangeParams.comment += `\n${exchangeSelectedFlightsInfo}`;
        mergedCreateExchangeParams.comment = mergedCreateExchangeParams.comment.trim();
    }
    yield put(setLoading(payload.isAutoCreate ? 'auto-create-exchange' : 'create-exchange'));
    try {
        const exchangeOrder = yield call(createExchangeMutation, mergedCreateExchangeParams);
        const exchangeOrderData = (_a = exchangeOrder === null || exchangeOrder === void 0 ? void 0 : exchangeOrder.data) === null || _a === void 0 ? void 0 : _a.CreateExchange;
        if (!exchangeOrderData || ((_b = exchangeOrder.errors) === null || _b === void 0 ? void 0 : _b.length)) {
            throw new Error((_d = (_c = exchangeOrder.errors) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.message);
        }
        yield put(clearExchangeVariants());
        yield put(setLastExchange(exchangeOrderData));
        if (payload.onSuccess) {
            yield call(payload.onSuccess, exchangeOrderData);
        }
    }
    catch (err) {
        const errorMessage = i18n.t(getGqlErrorMessage(err), { ns: 'Exchange' });
        yield put(setError(errorMessage));
    }
    yield put(stopLoading());
}
function* refuseExchangeOrderSaga({ payload }) {
    var _a, _b, _c, _d;
    const lastExchange = yield select(getLastExchange);
    yield put(setLoading('refuse-exchange'));
    try {
        const exchangeOrder = yield call(refuseExchangeMutation, lastExchange.id);
        const exchangeOrderData = (_a = exchangeOrder === null || exchangeOrder === void 0 ? void 0 : exchangeOrder.data) === null || _a === void 0 ? void 0 : _a.RefuseExare;
        if (!exchangeOrderData || ((_b = exchangeOrder.errors) === null || _b === void 0 ? void 0 : _b.length)) {
            throw new Error((_d = (_c = exchangeOrder.errors) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.message);
        }
        yield put(setLastExchange(exchangeOrderData));
        if (payload.onSuccess) {
            yield call(payload.onSuccess, exchangeOrderData);
        }
    }
    catch (err) {
        const errorMessage = i18n.t(getGqlErrorMessage(err), { ns: 'Exchange' });
        yield put(setError(errorMessage));
    }
    yield put(stopLoading());
}
export function* createInitialExchangeState({ payload }) {
    var _a, _b;
    const { global: { companyInfo: { iataCode } } } = yield getContext('config');
    const initialValue = shouldSkipPassengers((_b = (_a = payload.travellers) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0, iataCode);
    const actions = [
        setSegments(getInitialSegmentsState(payload.flight.segmentGroups)),
        setPassengers(getInitialPassengerState(payload.travellers, initialValue))
    ];
    yield put(batchActions(actions));
}
export default function* rootSaga() {
    yield all([
        segmentsSagas(),
        exchangeVariantsSagas(),
        takeLatest(CREATE_EXCHANGE_ORDER, createExchangeOrderSaga),
        takeLatest(REFUSE_EXCHANGE_ORDER, refuseExchangeOrderSaga)
    ]);
}
